import { useState, useEffect } from 'react';
import { ImageTransformOptions } from '../types';
import { buildResponsiveUrl, buildResizeUrl } from '../utils/urlBuilder';
import { getRescaleConfig } from '../utils/config';

type UseResponsiveImageProps = {
  src: string;
  options?: ImageTransformOptions;
  fallbackWidth?: number;
  placeholderSize?: number;
  placeholderQuality?: number;
};

type UseResponsiveImageResult = {
  isLoading: boolean;
  error: Error | null;
  data: {
    srcSet: string;
    src: string;
    placeholderSrc: string;
    sizes: string;
  } | null;
};

/**
 * Hook para obtener datos de imágenes responsivas
 */
export const useResponsiveImage = ({
  src,
  options = {},
  fallbackWidth,
  placeholderSize,
  placeholderQuality
}: UseResponsiveImageProps): UseResponsiveImageResult => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<UseResponsiveImageResult['data']>(null);
  
  const config = getRescaleConfig();
  
  useEffect(() => {
    if (!src) {
      setIsLoading(false);
      return;
    }
    
    let isMounted = true;
    
    const fetchResponsiveImageData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Obtener URL para imágenes responsivas
        const responsiveUrl = buildResponsiveUrl(src, options);
        
        // Obtener datos de imágenes responsivas
        const response = await fetch(responsiveUrl);
        
        if (!response.ok) {
          throw new Error(`Error al obtener imágenes responsivas: ${response.status}`);
        }
        
        const responseData = await response.json();
        
        // Fallback width para src principal
        const width = fallbackWidth || config.presets.medium.width;
        
        // Fallback src con el formato y calidad especificados en las opciones
        const format = options.format || config.defaultFormat;
        const quality = options.quality || config.defaultQuality;
        
        const fallbackSrc = buildResizeUrl(src, {
          ...options,
          width,
          format,
          quality
        });
        
        // Placeholder para lazy loading con mejor calidad para una experiencia mejorada
        const placeholderWidth = placeholderSize || config.placeholderWidth;
        // Usar calidad media para el placeholder para que no se vea pixelado
        const placeholderSrc = buildResizeUrl(src, {
          ...options,
          width: placeholderWidth,
          format, // Usar el mismo formato que la imagen principal
          quality: placeholderQuality || 50 // Calidad media por defecto (era 30)
        });
        
        if (isMounted) {
          setData({
            srcSet: responseData.srcset,
            src: fallbackSrc,
            placeholderSrc,
            sizes: config.defaultSizes
          });
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
          console.error("Error cargando imagen responsiva:", err);
          
          // Fallback en caso de error
          if (fallbackWidth) {
            const format = options.format || config.defaultFormat;
            const quality = options.quality || config.defaultQuality;
            
            const fallbackSrc = buildResizeUrl(src, {
              ...options,
              width: fallbackWidth,
              format,
              quality
            });
            
            setData({
              srcSet: '',
              src: fallbackSrc,
              placeholderSrc: fallbackSrc,
              sizes: config.defaultSizes
            });
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    fetchResponsiveImageData();
    
    return () => {
      isMounted = false;
    };
  }, [src, JSON.stringify(options), fallbackWidth, placeholderSize, placeholderQuality]);
  
  return { isLoading, error, data };
};

export default useResponsiveImage; 